import React, { useState, useMemo } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Chip,
  Typography,
  Paper,
  Container,
  Alert,
  useTheme,
  useMediaQuery,
  MobileStepper,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import StepConfig from './StepConfig';
import StepMes from './StepMes';
import StepBalanco from './StepBalanco';
import StepRevisao from './StepRevisao';
import {
  GRUPOS,
  gruposParaRespostas,
  camposDeMes,
  rotulosMeses,
  MESES_PADRAO
} from '../../config/planoContas';
import {
  manterMesesPreenchidos,
  realinharJanela,
  situacaoDaJanela
} from '../../services/janelaDeMeses';
import useLocalStorage from '../../hooks/useLocalStorage';
import { RESPOSTAS_VAZIAS } from '../QuestionarioDePerfil';
import { CHAVE_RASCUNHO_WIZARD, CHAVE_PASSO_WIZARD } from '../../config/chavesLocais';

const contasIniciais = (grupo, quantidade) =>
  grupo.contas.map((c) => ({
    ...c,
    ...Object.fromEntries(camposDeMes(quantidade).map((campo) => [campo, '']))
  }));

/**
 * O usuario respondeu alguma coisa do perfil?
 *
 * Enquanto nao respondeu, o questionario usa o catalogo padrao — e nao o
 * recorte de "ninguem respondeu nada", que e menor. Quem abre o wizard direto
 * precisa continuar encontrando a linha de mercadoria.
 */
const perfilRespondido = (perfil) => {
  if (!perfil) return false;
  return (perfil.vende || []).length > 0
    || (perfil.recebe || []).length > 0
    || perfil.temFuncionario !== null
    || perfil.temDivida !== null
    || (perfil.regime && perfil.regime !== 'naoSei')
    || (perfil.comoImposto && perfil.comoImposto !== 'naoSei')
    || perfil.profundidade === 'conta';
};

const estadoInicial = () => ({
  perfil: RESPOSTAS_VAZIAS,
  config: {
    empresa: '',
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear(),
    quantidadeMeses: MESES_PADRAO
  },
  grupos: Object.fromEntries(GRUPOS.map((g) => [g.id, contasIniciais(g, MESES_PADRAO)])),
  balanco: { preenchido: true, ativo: {}, passivo: {}, patrimonio: {} }
});

/**
 * Alguém já digitou alguma coisa? É o que decide se há rascunho a recuperar.
 *
 * A varredura é por PADRÃO de chave, e não por uma lista fixa de três campos:
 * um rascunho de doze meses lido por código que só conhecia `mes1..mes3` era
 * declarado vazio quando o usuário tinha preenchido só o segundo semestre — e o
 * "começar do zero" apagava trabalho de verdade.
 */
const CAMPO_DE_MES = /^mes\d+$/;

const temAlgoPreenchido = (dados) => {
  if (!dados) return false;
  if (dados.config?.empresa) return true;
  return Object.values(dados.grupos || {}).some((contas) =>
    (contas || []).some((c) =>
      Object.keys(c || {}).some(
        (campo) => CAMPO_DE_MES.test(campo) && String(c[campo] ?? '').trim() !== ''
      )
    )
  );
};

/**
 * Acima disto o Stepper de bolinhas deixa de caber e vira ruído.
 *
 * Ele desenha UM passo por mês: com 24 meses são 27 rótulos espremidos numa
 * linha, ilegíveis e sem utilidade nenhuma. Passando do teto, o desktop usa a
 * mesma leitura compacta do celular ("Passo 5 de 27"), que continua dizendo
 * onde a pessoa está sem prometer uma navegação que não existe ali.
 */
const MAXIMO_DE_PASSOS_NO_STEPPER = 10;

/**
 * Questionário guiado que reproduz o roteiro do consultor: percorre o plano
 * de contas na ordem da planilha, perguntando conta por conta o valor de
 * cada mês. Ao final entrega o mesmo formato de dados que a importação de
 * planilha, para que os dashboards funcionem sem saber a origem.
 *
 * A navegação é por MÊS, e não por grupo de contas. Era um passo por grupo com
 * uma coluna por mês; a grade não sobrevive a 6 nem a 12 meses, e o documento
 * que o empresário tem na mão é o extrato de abril, não a série anual da conta
 * "Aluguel". Cada página traz os sete grupos de um mês só, e o mês seguinte se
 * repete em um clique.
 *
 * PREENCHER MENOS DO QUE FOI OFERECIDO É NORMAL. Mês deixado em branco — nas
 * pontas ou no meio — sai da janela em `manterMesesPreenchidos` e não vira
 * lançamento nenhum: ele é uma lacuna declarada, e o histórico já sabe exibir
 * competência ausente como travessão. Nada além de faturamento zerado em TUDO
 * bloqueia o envio.
 *
 * O preenchimento é salvo a cada tecla. Um F5, um toque no botão voltar do
 * celular ou uma aba fechada por engano apagavam tudo, e quem estava no campo
 * 60 não recomeçava. Nenhum motor consertado salva um usuário que já desistiu.
 */
const Wizard = ({ onFinish, onCancel, aoVerPlanos = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const inicial = useMemo(estadoInicial, []);
  const [formData, setFormData] = useLocalStorage(CHAVE_RASCUNHO_WIZARD, inicial);

  // O CATALOGO DE PERGUNTAS SAI DAS RESPOSTAS DO PERFIL.
  //
  // Enquanto ninguem respondeu nada, continua o catalogo padrao: quem abre o
  // questionario direto nao pode perder a linha de mercadoria so por nao ter
  // passado pelo perfil.
  const catalogo = useMemo(
    () => (perfilRespondido(formData.perfil) ? gruposParaRespostas(formData.perfil) : GRUPOS),
    [formData.perfil]
  );

  /**
   * Troca o perfil sem PERDER o que ja foi digitado.
   *
   * Conta que sai do recorte novo mas ja tem valor CONTINUA na lista. Descartar
   * o que a pessoa digitou porque ela mudou uma resposta seria apagar medicao —
   * o oposto do que este produto faz em todo lugar.
   */
  const alterarPerfil = (perfil) => {
    setFormData((d) => {
      const novos = gruposParaRespostas(perfil);
      const quantidade = Number(d.config?.quantidadeMeses) || MESES_PADRAO;
      const campos = camposDeMes(quantidade);
      const temValor = (conta) => campos.some((campo) => String(conta[campo] ?? '').trim() !== '');

      const grupos = {};
      novos.forEach((grupo) => {
        const anteriores = d.grupos?.[grupo.id] || [];
        const porCodigo = new Map(anteriores.map((c) => [c.codigo, c]));
        const doRecorte = grupo.contas.map((conta) => porCodigo.get(conta.codigo)
          || { ...conta, ...Object.fromEntries(campos.map((campo) => [campo, ''])) });
        const codigosDoRecorte = new Set(grupo.contas.map((c) => c.codigo));
        const preservadas = anteriores.filter((c) => !codigosDoRecorte.has(c.codigo) && temValor(c));
        grupos[grupo.id] = [...doRecorte, ...preservadas];
      });

      // Grupo inteiro que saiu do recorte: so sobrevive se tiver valor.
      Object.entries(d.grupos || {}).forEach(([id, contas]) => {
        if (grupos[id]) return;
        const comValor = (contas || []).filter(temValor);
        if (comValor.length) grupos[id] = comValor;
      });

      return { ...d, perfil, grupos };
    });
  };
  const [activeStep, setActiveStep] = useLocalStorage(CHAVE_PASSO_WIZARD, 0);
  const [enviando, setEnviando] = useState(false);
  // O plano sobe do StepConfig, que é quem consulta `/api/planos` e
  // `/api/periodos`. Fica aqui porque a revisão final precisa dele para dizer o
  // que vai ser gravado e o que vai ser substituído — antes do clique.
  const [plano, setPlano] = useState(null);
  // Medido UMA vez, na montagem: depois da primeira tecla o rascunho deixa de
  // ser "recuperado" e passa a ser o que o usuário está digitando agora.
  const [rascunhoRecuperado, setRascunhoRecuperado] = useState(() => temAlgoPreenchido(formData));

  const quantidadeMeses = Number(formData.config.quantidadeMeses) || MESES_PADRAO;

  const meses = useMemo(
    () => rotulosMeses(formData.config.mes, formData.config.ano, quantidadeMeses),
    [formData.config.mes, formData.config.ano, quantidadeMeses]
  );

  // Configuração → uma página por MÊS → balanço → revisão
  const passos = useMemo(
    () => [
      { id: 'config', rotulo: 'Início' },
      ...meses.map((mes, i) => ({ id: `mes-${i}`, rotulo: mes.curto, mes, indice: i })),
      { id: 'balanco', rotulo: 'Balanço' },
      { id: 'revisao', rotulo: 'Revisão' }
    ],
    [meses]
  );

  // O passo salvo pode ter ficado fora do intervalo se a janela encolheu entre
  // a sessão que gravou o rascunho e esta.
  const passoAtual = Math.min(Math.max(activeStep, 0), passos.length - 1);
  const passo = passos[passoAtual];
  const ultimo = passoAtual === passos.length - 1;
  const progresso = Math.round((passoAtual / (passos.length - 1)) * 100);

  const descartarRascunho = () => {
    setFormData(estadoInicial());
    setActiveStep(0);
    setRascunhoRecuperado(false);
    window.scrollTo(0, 0);
  };

  /**
   * Trocar o tamanho da janela move os valores junto com o mês a que pertencem.
   * Sem realinhar, quem sobe de 3 para 6 meses veria o faturamento de junho
   * virar o de março: os campos são posicionais e a janela termina sempre no
   * mês de referência.
   */
  const alterarConfig = (config) =>
    setFormData((d) => {
      const de = Number(d.config.quantidadeMeses) || MESES_PADRAO;
      const para = Number(config.quantidadeMeses) || MESES_PADRAO;
      if (de === para) return { ...d, config };
      return { ...d, config, grupos: realinharJanela(d.grupos, de, para) };
    });

  const campos = camposDeMes(quantidadeMeses);
  const receitaTotal = (formData.grupos.receitas || []).reduce(
    (s, c) => s + campos.reduce((sm, campo) => sm + (Number(c[campo]) || 0), 0),
    0
  );

  // Quais meses da janela têm algum valor. Alimenta o atalho de navegação e a
  // revisão: mês em branco não é erro, é lacuna declarada.
  const janela = situacaoDaJanela({ ...formData, meses });
  const camposPreenchidos = new Set(janela.preenchidos.map((m) => m.campo));

  const irParaPasso = (indice) => {
    window.scrollTo(0, 0);
    setActiveStep(indice);
  };

  const handleNext = async () => {
    if (!ultimo) {
      irParaPasso(passoAtual + 1);
      return;
    }
    setEnviando(true);
    try {
      // Quem descarta o rascunho é `handleManualEntryFinish`, no dashboard.
      // Fazê-lo aqui não funciona: gerar o dashboard troca a tela e desmonta
      // este componente antes de o `useLocalStorage` persistir o estado zerado.
      // E enquanto o envio não conclui, o rascunho é o único lugar onde os
      // campos preenchidos existem.
      const { formData: apenasPreenchidos } = manterMesesPreenchidos({ ...formData, meses });
      await onFinish(apenasPreenchidos);
    } finally {
      setEnviando(false);
    }
  };

  const handleBack = () => irParaPasso(passoAtual - 1);

  // Só duas coisas impedem o envio, e nenhuma delas é mês em branco: janela
  // acima do que o plano permite escolher, e faturamento zerado em todos os
  // meses — sem receita não existe margem, ponto de equilíbrio nem lucro.
  const janelaBloqueada = plano ? plano.janelaLiberada === false : false;
  const bloqueado = janelaBloqueada || (ultimo && receitaTotal === 0);

  /**
   * Atalho para saltar direto ao mês que a pessoa tem em mãos.
   *
   * Com janela livre até 24 meses, quem conhece só setembro precisaria clicar
   * "Próximo" oito vezes sobre páginas que vai deixar em branco. O atalho mostra
   * de relance quais meses já receberam valor — é a mesma informação que a
   * revisão usa para dizer o que fica de fora.
   */
  const renderAtalhoDeMeses = () => {
    if (meses.length < 2) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Ir para o mês — os marcados já têm valor
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.5 }}>
          {meses.map((mes, i) => {
            const preenchido = camposPreenchidos.has(mes.campo);
            return (
              <Chip
                key={mes.campo}
                size="small"
                label={mes.curto}
                aria-label={`Ir para ${mes.longo}`}
                onClick={() => irParaPasso(i + 1)}
                color={passo.indice === i ? 'primary' : preenchido ? 'success' : 'default'}
                variant={passo.indice === i || preenchido ? 'filled' : 'outlined'}
                sx={{ flexShrink: 0 }}
              />
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderPasso = () => {
    if (passo.id === 'config') {
      return (
        <StepConfig
          config={formData.config}
          onChange={alterarConfig}
          aoVerPlanos={aoVerPlanos}
          aoSaberDoPlano={setPlano}
          catalogo={catalogo}
          perfil={formData.perfil}
          aoMudarPerfil={alterarPerfil}
        />
      );
    }

    if (passo.id === 'balanco') {
      return (
        <StepBalanco
          balanco={formData.balanco}
          onChange={(balanco) => setFormData((d) => ({ ...d, balanco }))}
        />
      );
    }

    if (passo.id === 'revisao') {
      return (
        <StepRevisao
          formData={formData}
          meses={meses}
          preenchidos={janela.preenchidos}
          descartados={janela.descartados}
          plano={plano}
          catalogo={catalogo}
        />
      );
    }

    return (
      <>
        {renderAtalhoDeMeses()}
        <StepMes
          mes={passo.mes}
          mesAnterior={passo.indice > 0 ? meses[passo.indice - 1] : null}
          indice={passo.indice}
          total={meses.length}
          grupos={formData.grupos}
          quantidadeMeses={quantidadeMeses}
          onChange={(grupos) => setFormData((d) => ({ ...d, grupos }))}
          catalogo={catalogo}
        />
      </>
    );
  };

  return (
    // No celular o respiro lateral somava TRÊS camadas — 16px do casco + 16px
    // de gutter do Container + 16px do Paper = 48px de cada lado, quase 100px
    // perdidos numa tela de 390. O Container perde os gutters e o Paper cai
    // para 8px; o que falta para colar na borda (o casco) é zerado por quem
    // renderiza o Wizard, via `sxDoConteudo`. Desktop inalterado.
    <Container maxWidth="lg" disableGutters sx={{ px: { xs: 0, sm: 0 }, py: { xs: 0, md: 4 } }}>
      <Paper elevation={2} sx={{ p: { xs: 1, sm: 3, md: 4 }, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {/* Mesmo caso do Fechar o mês: `h5` sem `component` rendia um <h5>
              solto, e o questionário — que é uma das duas portas de entrada do
              produto — não tinha título nenhum na hierarquia. */}
          <Typography variant="h4" component="h1" fontWeight="bold">
            Preenchimento guiado
          </Typography>
          <Button color="inherit" onClick={onCancel} disabled={enviando}>
            Cancelar
          </Button>
        </Box>

        <LinearProgress variant="determinate" value={progresso} sx={{ mb: 3, borderRadius: 1 }} />

        {rascunhoRecuperado && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={descartarRascunho} disabled={enviando}>
                Começar do zero
              </Button>
            }
          >
            Recuperamos o que você já tinha preenchido. Continue de onde parou.
          </Alert>
        )}

        {!isMobile && passos.length <= MAXIMO_DE_PASSOS_NO_STEPPER ? (
          <Stepper activeStep={passoAtual} alternativeLabel sx={{ mb: 4 }}>
            {passos.map((p) => (
              <Step key={p.id}>
                <StepLabel>{p.rotulo}</StepLabel>
              </Step>
            ))}
          </Stepper>
        ) : (
          !isMobile && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Passo {passoAtual + 1} de {passos.length} · {passo.rotulo}
            </Typography>
          )
        )}

        <Box sx={{ minHeight: 320 }}>{renderPasso()}</Box>

        {janelaBloqueada && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            A quantidade de meses escolhida está acima do que o seu plano permite. Volte ao
            início e reduza a janela, ou assine para levantar mais meses de uma vez.
          </Alert>
        )}

        {ultimo && receitaTotal === 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            Informe o faturamento de pelo menos um mês antes de gerar o dashboard.
          </Alert>
        )}

        {isMobile ? (
          <MobileStepper
            variant="text"
            steps={passos.length}
            position="static"
            activeStep={passoAtual}
            sx={{ mt: 3, bgcolor: 'transparent' }}
            nextButton={
              // No celular o rótulo continuava 'Gerar dashboard' durante o
              // envio: o empresário usa no celular, e era justamente lá que o
              // botão não avisava que estava trabalhando.
              <Button size="small" onClick={handleNext} disabled={enviando || bloqueado}>
                {ultimo ? (enviando ? 'Gerando…' : 'Gerar dashboard') : 'Próximo'}
                {enviando && ultimo ? <CircularProgress size={14} sx={{ ml: 0.5 }} /> : <KeyboardArrowRight />}
              </Button>
            }
            backButton={
              <Button size="small" onClick={handleBack} disabled={passoAtual === 0 || enviando}>
                <KeyboardArrowLeft />
                Voltar
              </Button>
            }
          />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={passoAtual === 0 || enviando}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              endIcon={ultimo ? <CheckCircleOutlineIcon /> : <ArrowForwardIcon />}
              onClick={handleNext}
              disabled={enviando || bloqueado}
            >
              {ultimo ? (enviando ? 'Gerando…' : 'Gerar dashboard') : 'Próximo'}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Wizard;
